import { useTranslation } from 'react-i18next';
import { TimeSlot } from '../../types';
import { useHourFormat } from '../../hooks/useHourFormat';
import { formatTimeFromDate } from '../../utils/locale';
import { AvailabilityRing } from './AvailabilityRing';
import { SlotParticipantDetail } from './SlotParticipantDetail';

interface HeroSlotCardProps {
  slot: TimeSlot;
  totalParticipants: number;
  allParticipantNames: string[];
  participantName: string | null;
  isOpen: boolean;
  onToggle: () => void;
}

const getLocaleFromLanguage = (lang: string): string => {
  const localeMap: Record<string, string> = {
    en: 'en-US',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    ru: 'ru-RU',
  };
  return localeMap[lang] || 'en-US';
};

export function HeroSlotCard({
  slot,
  totalParticipants,
  allParticipantNames,
  participantName,
  isOpen,
  onToggle,
}: HeroSlotCardProps) {
  const { i18n } = useTranslation();
  const { hourFormat } = useHourFormat();
  const locale = getLocaleFromLanguage(i18n.language);

  const startDate = new Date(slot.utc_start * 1000);
  const endDate = new Date(slot.utc_end * 1000);

  const heroDate = startDate
    .toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' })
    .toUpperCase();
  const startHour = formatTimeFromDate(startDate, i18n.language, hourFormat);
  const endHour = formatTimeFromDate(endDate, i18n.language, hourFormat);

  const total = totalParticipants > 0 ? totalParticipants : slot.count;
  const pct = total > 0 ? Math.round((slot.count / total) * 100) : 0;

  return (
    <div className="mb-3.5 rounded-3xl border border-room-accent/35 bg-room-surface p-5 [background-image:linear-gradient(160deg,rgba(47,211,184,0.13),rgba(47,211,184,0.02)_55%)]">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[13px] font-bold tracking-wide text-room-text-dim">{heroDate}</div>
          <div className="mt-0.5 text-[28px] font-extrabold tracking-tight">
            {startHour} – {endHour}
          </div>
        </div>
        <AvailabilityRing pct={pct} isOpen={isOpen} onToggle={onToggle} />
      </div>
      <SlotParticipantDetail
        participantNames={allParticipantNames}
        availableNames={slot.participants}
        currentParticipantName={participantName}
        isOpen={isOpen}
      />
    </div>
  );
}
